/**
 * Вхід — окремий документ (login.html → src/login.tsx, свій чанк у бюджеті ≤ 100 КБ): у SPA його
 * коду немає. Перехід на /login зсередини застосунку (пункт «Увійти», 401 на екрані) — повним
 * завантаженням без запису в історії (`location.replace`): «Назад» зі сторінки входу веде на
 * попередній екран, а не знову на вхід. Поки документ змінюється, лишається попередній екран. Якщо ж сервер віддав
 * застосунок на сам /login (edge без маршруту входу), — «не знайдено», а не нескінченне
 * перезавантаження.
 *
 * `?next=` — куди повернутись після входу (UX-06). Він може прийти з чужого посилання, тож далі
 * йде лише внутрішній шлях (`safeNextPath`); решта значень мовчки відкидається. Поруч — мітка
 * людини (`bindNext`): чужому, хто ввійде в цій вкладці, `next` не діє.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRoute, notFound } from '@tanstack/react-router';
import { type Me, platformKeys } from '../../platform/api.ts';
import { storedRecentOwner } from '../../platform/recent.ts';
import { bindNext } from '../next-owner.ts';
import { loginHref, safeNextPath } from '../next-path.ts';
import { LOGIN_PATH, rootRoute } from './root.tsx';

/** Шлях, з яким завантажився документ застосунку. */
const BOOT_PATH = typeof location === 'undefined' ? '' : location.pathname;

export interface LoginSearch {
  next?: string;
}

/**
 * `next` — для тієї самої людини (app/next-owner.ts). Хто вона: `/me` у кеші, а коли його ще
 * немає (вкладку перезавантажено вже без сесії) — власник недавніх записів цього пристрою.
 */
export function bindLoginNext(next: unknown, queryClient: QueryClient): void {
  bindNext(
    safeNextPath(next),
    queryClient.getQueryData<Me>(platformKeys.me)?.id ?? storedRecentOwner(),
  );
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: LOGIN_PATH,
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const next = safeNextPath(search.next);
    return next ? { next } : {};
  },
  beforeLoad: ({ preload, search, context }) => {
    if (preload) return;
    if (BOOT_PATH === LOGIN_PATH) throw notFound();
    bindLoginNext(search.next, context.queryClient);
    location.replace(loginHref(search.next));
    return new Promise<never>(() => undefined);
  },
});
